import type { Editor } from '@tiptap/react';
import { create } from 'zustand';

export type ContrastMode = 'none' | 'drop-shadow' | 'glow' | 'outline' | 'bg-box';

export interface ContrastPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface TextElement {
  id: string;
  text: string;
  x: number;
  y: number;
  width: number;
  /** User-set height from bounding box resize. When set, V-Align uses this as
   *  the container height instead of auto-sizing to content. Undefined = auto. */
  height?: number;
  fontSize: number;
  fontFamily: string;
  fontWeight: number;
  isItalic: boolean;
  isUnderline: boolean;
  textAlign: 'left' | 'center' | 'right';
  verticalAlign: 'top' | 'middle' | 'bottom';
  textTransform: 'none' | 'uppercase';
  lineHeight: number;
  letterSpacing: number;
  fill: string | { type: 'linear'; colors: string[]; angle: number };
  contrastMode: ContrastMode;
  contrastColor: string;
  contrastIntensity: number;
  contrastPadding: ContrastPadding;
  /** Box background corner roundness 0-100%. 0 = sharp, 100 = pill. */
  boxRoundness: number;
  /** Shape type for bg-box mode. Default 'rectangle' for backward compatibility. */
  boxShapeType?: 'rectangle' | 'circle' | 'ellipse' | 'star' | 'triangle';
  /** Feather intensity for bg-box edges (0-100). Default 0. */
  boxFeather?: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  zIndex?: number;
}

export interface LogoElement {
  id: string;
  kind: 'logo';
  logoUrl: string;
  logoVariant: 'primary' | 'light' | 'dark';
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
  /** 0–1. Default 1 (fully opaque). */
  opacity: number;
  zIndex?: number;
}

export function isLogoElement(el: AnyElement): el is LogoElement {
  return 'kind' in el && (el as LogoElement).kind === 'logo';
}

export interface ShapeElement {
  id: string;
  kind: 'shape';
  shapeType: 'rectangle' | 'circle' | 'ellipse' | 'star' | 'triangle' | 'line';
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  fill: string | { type: 'linear' | 'radial'; colors: [string, string]; angle?: number };
  opacity: number;
  feather: number;
  cornerRadius?: number;
  points?: number;
  innerRadius?: number;
  strokeWidth?: number;
  strokeColor?: string;
  zIndex: number;
}

export type AnyElement = TextElement | LogoElement | ShapeElement;

export function isShapeElement(el: AnyElement): el is ShapeElement {
  return 'kind' in el && (el as ShapeElement).kind === 'shape';
}

/** Get the zIndex of any element, defaulting to 0 for legacy elements without it. */
export function getZIndex(el: AnyElement): number {
  const z = (el as { zIndex?: number }).zIndex;
  return typeof z === 'number' ? z : 0;
}

/** Resolve fill to a simple CSS color string (for non-canvas contexts). */
export function resolveFillColor(fill: TextElement['fill']): string {
  return typeof fill === 'string' ? fill : (fill.colors[0] ?? '#ffffff');
}

interface TextEditorState {
  elements: AnyElement[];
  selectedId: string | null;
  activeEditor: Editor | null;
  past: AnyElement[][];
  future: AnyElement[][];
  /** Number of KonvaTextNode rasterizations currently in flight. */
  rasterizationPending: number;
  /** True while handleSave is running — prevents NativeTextarea onBlur from deleting elements. */
  isSavingGlobal: boolean;
  addElement: (element: AnyElement) => void;
  /**
   * Update an element. By default pushes a snapshot to the undo stack.
   * Pass `{ skipHistory: true }` to suppress — used during TipTap inline
   * editing so every keystroke doesn't flood the store's undo history
   * (TipTap maintains its own per-character undo internally).
   */
  updateElement: (
    id: string,
    updates: Partial<TextElement> | Partial<LogoElement> | Partial<ShapeElement>,
    options?: { skipHistory?: boolean },
  ) => void;
  deleteElement: (id: string) => void;
  selectElement: (id: string | null) => void;
  setActiveEditor: (editor: Editor | null) => void;
  clearElements: () => void;
  loadElements: (elements: AnyElement[]) => void;
  undo: () => void;
  redo: () => void;
  incrementRasterPending: () => void;
  decrementRasterPending: () => void;
  setIsSavingGlobal: (saving: boolean) => void;
  resetRasterizationPending: () => void;
  bringToFront: (id: string) => void;
  sendToBack: (id: string) => void;
  bringForward: (id: string) => void;
  sendBackward: (id: string) => void;
}

export const TEXT_PLACEHOLDER = 'Double click to edit';

export function isPlaceholderText(text: string): boolean {
  return text.replace(/<[^>]*>/g, '').trim() === TEXT_PLACEHOLDER;
}

export const useTextEditorStore = create<TextEditorState>((set, get) => ({
  elements: [],
  selectedId: null,
  activeEditor: null,
  past: [],
  future: [],
  rasterizationPending: 0,
  isSavingGlobal: false,
  addElement: (element) =>
    set((state) => {
      const maxZ = state.elements.reduce((max, el) => Math.max(max, getZIndex(el)), 0);
      const withZ = { ...element, zIndex: maxZ + 1 };
      return {
        past: [...state.past, state.elements].slice(-50),
        future: [],
        elements: [...state.elements, withZ],
        selectedId: element.id,
      };
    }),
  updateElement: (id, updates, options) =>
    set((state) => ({
      past: options?.skipHistory ? state.past : [...state.past, state.elements].slice(-50),
      future: options?.skipHistory ? state.future : [],
      elements: state.elements.map((el) => (el.id === id ? { ...el, ...updates } : el)),
    })),
  deleteElement: (id) =>
    set((state) => ({
      past: [...state.past, state.elements].slice(-50),
      future: [],
      elements: state.elements.filter((el) => el.id !== id),
      selectedId: state.selectedId === id ? null : state.selectedId,
    })),
  selectElement: (id) => set({ selectedId: id }),
  setActiveEditor: (editor) => set({ activeEditor: editor }),
  clearElements: () =>
    set({
      past: [],
      future: [],
      elements: [],
      selectedId: null,
      activeEditor: null,
      rasterizationPending: 0,
      isSavingGlobal: false,
    }),
  loadElements: (elements) => {
    // Normalise zIndex to compact integers (0, 1, 2…) preserving relative stacking order.
    // Prevents silent no-ops in bringForward/sendBackward when elements share a zIndex
    // (e.g. legacy layers where zIndex was undefined → all resolve to 0 via getZIndex).
    const sorted = [...elements].sort((a, b) => getZIndex(a) - getZIndex(b));
    const newZ = new Map(sorted.map((el, i) => [el.id, i]));
    set({
      past: [],
      future: [],
      elements: elements.map((el) => ({ ...el, zIndex: newZ.get(el.id) ?? 0 })) as AnyElement[],
      selectedId: null,
      activeEditor: null,
      rasterizationPending: 0,
      isSavingGlobal: false,
    });
  },
  undo: () => {
    const state = get();
    if (state.past.length === 0) return;
    const previous = state.past[state.past.length - 1];
    const newPast = state.past.slice(0, state.past.length - 1);
    set({
      past: newPast,
      future: [state.elements, ...state.future].slice(0, 50),
      elements: previous,
    });
  },
  redo: () => {
    const state = get();
    if (state.future.length === 0) return;
    const next = state.future[0];
    const newFuture = state.future.slice(1);
    set({
      past: [...state.past, state.elements].slice(-50),
      future: newFuture,
      elements: next,
    });
  },
  incrementRasterPending: () =>
    set((state) => ({ rasterizationPending: state.rasterizationPending + 1 })),
  decrementRasterPending: () =>
    set((state) => ({ rasterizationPending: Math.max(0, state.rasterizationPending - 1) })),
  setIsSavingGlobal: (saving) => set({ isSavingGlobal: saving }),
  resetRasterizationPending: () => set({ rasterizationPending: 0 }),
  bringToFront: (id) =>
    set((state) => {
      const maxZ = state.elements.reduce((max, el) => Math.max(max, getZIndex(el)), 0);
      return {
        past: [...state.past, state.elements].slice(-50),
        future: [],
        elements: state.elements.map((el) => (el.id === id ? { ...el, zIndex: maxZ + 1 } : el)),
      };
    }),
  sendToBack: (id) =>
    set((state) => {
      const minZ = state.elements.reduce((min, el) => Math.min(min, getZIndex(el)), 0);
      return {
        past: [...state.past, state.elements].slice(-50),
        future: [],
        elements: state.elements.map((el) => (el.id === id ? { ...el, zIndex: minZ - 1 } : el)),
      };
    }),
  bringForward: (id) =>
    set((state) => {
      const target = state.elements.find((el) => el.id === id);
      if (!target) return state;
      const targetZ = getZIndex(target);
      // Find the element with the next-higher zIndex
      const nextHigher = state.elements
        .filter((el) => el.id !== id && getZIndex(el) > targetZ)
        .sort((a, b) => getZIndex(a) - getZIndex(b))[0];
      if (!nextHigher) return state;
      const nextHigherZ = getZIndex(nextHigher);
      return {
        past: [...state.past, state.elements].slice(-50),
        future: [],
        elements: state.elements.map((el) => {
          if (el.id === id) return { ...el, zIndex: nextHigherZ };
          if (el.id === nextHigher.id) return { ...el, zIndex: targetZ };
          return el;
        }),
      };
    }),
  sendBackward: (id) =>
    set((state) => {
      const target = state.elements.find((el) => el.id === id);
      if (!target) return state;
      const targetZ = getZIndex(target);
      // Find the element with the next-lower zIndex
      const nextLower = state.elements
        .filter((el) => el.id !== id && getZIndex(el) < targetZ)
        .sort((a, b) => getZIndex(b) - getZIndex(a))[0];
      if (!nextLower) return state;
      const nextLowerZ = getZIndex(nextLower);
      return {
        past: [...state.past, state.elements].slice(-50),
        future: [],
        elements: state.elements.map((el) => {
          if (el.id === id) return { ...el, zIndex: nextLowerZ };
          if (el.id === nextLower.id) return { ...el, zIndex: targetZ };
          return el;
        }),
      };
    }),
}));
